/**
 * Number/date formatting helpers. All figures render with tabular-nums via the
 * `.num` / `.num-cell` utilities — these helpers only produce strings.
 */

/**
 * The app's ONE minus glyph: U+2212 MINUS SIGN.
 *
 * `Intl.NumberFormat` emits an ASCII hyphen-minus (U+002D), which is narrower
 * than the digits beside it and sits at a different height — in a tabular-nums
 * column it reads as a dash, not a sign. The app used to carry both: budgets
 * and the P&L hand-wrote `−`, the agreements margin column patched Intl's output
 * with a regex, and everything else printed whatever Intl gave it. Two minus
 * signs in one KPI strip is a typography bug the reader notices before they can
 * name it.
 *
 * So the rule is enforced HERE, in the formatters, not at the call site: no
 * function in this file can return an ASCII hyphen in front of a number. A new
 * screen gets it right without knowing the rule exists.
 *
 * CSV export is deliberately NOT formatted through these helpers — a
 * spreadsheet cannot add up `−1,234.56`. See `lib/csv.ts`.
 */
export const MINUS = '−';

/** Swap Intl's ASCII hyphen-minus for the real minus sign. */
function minusGlyph(formatted: string): string {
  return formatted.includes('-') ? formatted.replace('-', MINUS) : formatted;
}

const moneyFormatters = new Map<string, Intl.NumberFormat>();

function moneyFormatter(currency: string, decimals: number): Intl.NumberFormat {
  const key = `${currency}:${decimals}`;
  let formatter = moneyFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    moneyFormatters.set(key, formatter);
  }
  return formatter;
}

export interface MoneyOptions {
  /** Org currency code; defaults to USD. */
  currency?: string;
  /** Render negatives as ($1,234.56) instead of -$1,234.56. */
  parens?: boolean;
  /** Drop cents (whole dollars) — good for dashboards/charts. */
  whole?: boolean;
}

export function formatMoney(amount: number, options: MoneyOptions = {}): string {
  const { currency = 'USD', parens = false, whole = false } = options;
  const decimals = whole ? 0 : 2;
  if (parens && amount < 0) {
    return `(${moneyFormatter(currency, decimals).format(Math.abs(amount))})`;
  }
  return minusGlyph(moneyFormatter(currency, decimals).format(amount));
}

/** Compact money for chart axes: $12.5k, $1.2M. */
export function formatMoneyCompact(amount: number, currency = 'USD'): string {
  return minusGlyph(
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(amount),
  );
}

/**
 * A **variance or delta** — the figure whose sign is the point. Always carries
 * an explicit `+` or `−`; zero carries neither, because "no change" is not a
 * direction. Absolute amounts use `formatMoney(v, { parens: true })` instead:
 * parentheses for what something IS, a signed number for how much it MOVED.
 */
export function formatMoneySigned(value: number, options: MoneyOptions = {}): string {
  const magnitude = formatMoney(Math.abs(value), { ...options, parens: false });
  if (value === 0) return magnitude;
  return `${value > 0 ? '+' : MINUS}${magnitude}`;
}

/** The percent half of the same rule: +12.4% / −12.4% / 0%. */
export function formatPercentSigned(
  value: number,
  options: { decimals?: number; already100?: boolean; fixed?: boolean } = {},
): string {
  const magnitude = formatPercent(Math.abs(value), options);
  if (value === 0) return magnitude;
  return `${value > 0 ? '+' : MINUS}${magnitude}`;
}

export interface HoursOptions {
  /**
   * Pad to exactly 2 decimals (7.50, 1,234.25, 493.00). Use in right-aligned
   * numeric columns so a stack of hours reads as one financial column instead
   * of a ragged 2 / 5.25 / 493 / 449.5.
   */
  fixed?: boolean;
  /**
   * Append the hours unit: `21.5 h`. **This is the only hour unit in the app.**
   * "hrs" and "hours" are not alternatives — the same 21.5 hours appearing as
   * "21.5 hrs" on the dashboard and "21.5 h" on the support report is exactly
   * how one product starts reading like three. Use the plain word "hours" only
   * inside a running sentence.
   */
  unit?: boolean;
}

/** Hours with 0–2 decimals: 7.5, 1,234.25 (or exactly 2 with `fixed`). */
export function formatHours(hours: number, options: HoursOptions = {}): string {
  const value = minusGlyph(
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: options.fixed ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(hours),
  );
  return options.unit ? `${value} h` : value;
}

/**
 * 0.876 → 87.6% (value is a ratio 0–1 unless `already100` is set).
 *
 * `fixed` pads to exactly `decimals` places. Use it in right-aligned numeric
 * columns: without it a stack reads 66% / 66.9% / 67% / 67.2% and the decimal
 * points no longer line up, which is not good enough for financial software.
 */
export function formatPercent(
  value: number,
  options: { decimals?: number; already100?: boolean; fixed?: boolean } = {},
): string {
  const { decimals = 1, already100 = false, fixed = false } = options;
  const pct = already100 ? value : value * 100;
  return `${minusGlyph(
    new Intl.NumberFormat('en-US', {
      minimumFractionDigits: fixed ? decimals : 0,
      maximumFractionDigits: decimals,
    }).format(pct),
  )}%`;
}

/** "Aug" for the current year, "Apr 2025" otherwise — compact period tags. */
export function formatMonthShort(value: string, now = new Date()): string {
  const date = toDate(/^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    ...(date.getUTCFullYear() === now.getFullYear() ? {} : { year: 'numeric' }),
  }).format(date);
}

function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  // Date-only strings (YYYY-MM-DD) parse as UTC — pin to noon UTC so the
  // rendered day never shifts across timezones.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(`${value}T12:00:00Z`);
  return new Date(value);
}

/** Jan 5, 2026 */
export function formatDate(value: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(toDate(value));
}

/** Jan 5 */
export function formatDateShort(value: string | Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(toDate(value));
}

/** Jan 2026 (month columns in P&L, forecast) */
export function formatMonth(value: string | Date): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(toDate(value));
}
